import * as React from 'react';
import FormRow from 'Components/FormRow';
import InputHandler from 'Components/InputHandler';
import BlockWrapper from 'Components/_NewElements/BlockWrapper';
import { pdq8 } from 'neuro-schemas';
import { arrayOfAll, isNumber } from 'neuro-utils';
import { formatPartialDate } from 'Utility/date';
import { useMyServiceContext } from '../..';
import colors from '../../../../../config/theme/colors';
import browserLanguage from 'Utility/browserLanguage';
import { useAppSelector } from 'Store/index';

const { calculateProgress } = pdq8;

const fields = arrayOfAll.keys<Omit<IPDQ8, 'date'>>()([
  'difficultyGettingAroundInPublic',
  'difficultyDressingYourself',
  'feltDepressed',
  'problemsWithYourClosePersonalRelationships',
  'problemsWithYourConcentrationWhenReadingOrWatchingTV',
  'unableToCommunicateWithPeopleProperly',
  'painfulMuscleCrampsOrSpasms',
  'embarrassedInPublicDueToHavingParkinsonsDisease',
]);

export const PDQ8Form = (): React.JSX.Element => {
  const { editing, setEditingData, viewing } = useMyServiceContext<'pdq8'>();
  const locale = useAppSelector(s => s.settings.userSettings.uiLanguage) ?? browserLanguage;
  const pdq8Data = editing?.data ?? viewing?.data ?? {};
  const pdq8Locales = pdq8.getLocalizations(locale);

  const onChangeSurveyForm = (values: TOnChangeValues): void => {
    const [field, value] = Object.entries(values)[0] ?? [];
    if (!field) return;
    setEditingData?.({
      ...pdq8Data,
      [field]: value,
    });
  };

  return (
    <>
      <BlockWrapper title={viewing ? formatPartialDate(pdq8Data.date) : undefined}>
        {!viewing && (
          <FormRow title={'general.date'}>
            <InputHandler
              name="date"
              type="PartialDate"
              dateDefault="now"
              formData={{
                document: { date: pdq8Data.date },
                onChange: onChangeSurveyForm,
              }}
              editing={!!editing}
            />
          </FormRow>
        )}
        <FormRow bottomMargin={false} condition={!!viewing}>
          <div style={{ color: colors.tertiaryText, marginBottom: '1rem' }}>
            {['never', 'occassionally', 'sometimes', 'often', 'alwaysOrCannotDoAtAll'].map((opt, i) => {
              return <span key={opt}>{(i === 0 ? '' : ' / ') + pdq8Locales[`opts.${opt}`]}</span>;
            })}
          </div>
        </FormRow>
        <div style={{ marginBottom: '3rem' }}>
          {fields.map(f => {
            return (
              <FormRow key={f} title={pdq8Locales[f]} formatTitle={false}>
                <InputHandler
                  name={f}
                  type="Radio"
                  editing={!!editing}
                  options={['never', 'occassionally', 'sometimes', 'often', 'alwaysOrCannotDoAtAll']}
                  optionFormatter={o => pdq8Locales[`opts.${o}`] ?? '-'}
                  formData={{
                    document: { [f]: pdq8Data[f] },
                    onChange: onChangeSurveyForm,
                  }}
                />
              </FormRow>
            );
          })}
        </div>
        <FormRow title="myService.score">
          {(() => {
            const { yielded, ceiling } = calculateProgress(pdq8Data);
            if (!isNumber(yielded) || !isNumber(ceiling)) return '-';
            return `${Math.round((yielded / ceiling) * 100)} %`;
          })()}
        </FormRow>
      </BlockWrapper>
    </>
  );
};
